import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaLock } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { getAccountById } from '../../services/firestoreService';
import {
  StartLockedOverviewRow,
  StartLockedWhatsNewCard,
  StartLockedImplementationCard,
  StartLockedHiringStatusCard,
} from './StartLockedPrimitives';

/**
 * Locked preview of a portal department hub for /start prospects.
 *
 * Mirrors the real portal hub layout (Finance.js et al): Schedule Call · centered
 * title · Ask Question, then the tab nav, then the "All" tab body. The tabs come
 * from the portal's own `src/utils/*Tabs.js` configs, so this preview shows the
 * real section names and cannot drift as the portal changes.
 *
 * Everything is locked except one tab: an account may have a single `freePage`
 * granted from the CRM (LeadOverview), which unlocks that page as a free sample.
 * `unlockMap` declares which tab a freePage value corresponds to and where it
 * links under /start — tabs with no /start equivalent stay locked. An entry may
 * be one `{ freePage, to }` grant or an array of them, for tabs reachable via
 * more than one grant (e.g. Sales Materials vs the legacy Partner Programs).
 */

const headerBtnBase =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';

const StartLockedDepartmentPage = ({
  title,
  tabs,
  allTabs,
  unlockMap = {},
  showGoals = true,
  rightCard = 'implementation',
}) => {
  const { currentUser } = useAuth();
  const { isMobile } = useResponsive();
  const [freePage, setFreePage] = useState(null);

  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) return;
        const accountId = userDoc.data().accountId;
        if (!accountId) return;
        const accountData = await getAccountById(accountId);
        if (!cancelled && accountData?.freePage) setFreePage(accountData.freePage);
      } catch (error) {
        console.error(`Start ${title}: error loading freePage`, error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, title]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // The single tab this account has been granted, if any.
  const grantsFor = (tabId) => {
    const entry = unlockMap[tabId];
    if (!entry) return [];
    return Array.isArray(entry) ? entry : [entry];
  };
  const unlockedTabId = Object.keys(unlockMap).find((tabId) =>
    grantsFor(tabId).some((grant) => grant.freePage === freePage)
  );

  // Some grants (e.g. Proposals) map to a tab the portal hides from its own nav.
  // Surface it here anyway, otherwise the granted page would be unreachable.
  const hiddenUnlockedTab =
    unlockedTabId && !tabs.some((tab) => tab.id === unlockedTabId)
      ? (allTabs || []).find((tab) => tab.id === unlockedTabId)
      : null;
  const visibleTabs = hiddenUnlockedTab ? [...tabs, hiddenUnlockedTab] : tabs;

  return (
    <div className="p-6 md:p-8 text-gray-900 dark:text-white">
      <div className={`relative mb-6 ${isMobile ? 'pt-10' : ''}`}>
        <span
          className={`absolute top-0 left-0 ${headerBtnBase} text-gray-400 dark:text-gray-500 cursor-not-allowed`}
          title="Schedule a Strategy Call — available to members"
        >
          <span className="text-base leading-none shrink-0" aria-hidden="true">📞</span>
          {!isMobile && <span>Schedule Call</span>}
          <FaLock className="text-[10px] shrink-0" aria-hidden="true" />
        </span>

        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">{title}</h1>
        </div>

        <span
          className={`absolute top-0 right-0 ${headerBtnBase} text-gray-400 dark:text-gray-500 cursor-not-allowed`}
          title="Ask a Question — available to members"
        >
          <span className="text-base leading-none shrink-0" aria-hidden="true">💬</span>
          {!isMobile && <span>Ask Question</span>}
          <FaLock className="text-[10px] shrink-0" aria-hidden="true" />
        </span>
      </div>

      <nav className="flex flex-wrap justify-center gap-2 mb-6 px-2" aria-label={`${title} sections`}>
        {visibleTabs.map((tab, index) => {
          const unlocked = tab.id === unlockedTabId;
          if (unlocked) {
            return (
              <Link
                key={tab.id}
                to={grantsFor(tab.id).find((grant) => grant.freePage === freePage).to}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                {tab.emoji && (
                  <span className="text-base leading-none shrink-0" aria-hidden="true">{tab.emoji}</span>
                )}
                {tab.label}
              </Link>
            );
          }
          return (
            <span
              key={tab.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                index === 0
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800'
                  : 'bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400'
              }`}
            >
              {tab.emoji && (
                <span className="text-base leading-none shrink-0" aria-hidden="true">{tab.emoji}</span>
              )}
              {tab.label}
              {index !== 0 && <FaLock className="text-[10px] opacity-70 shrink-0" aria-hidden="true" />}
            </span>
          );
        })}
      </nav>

      <div className="mb-8 min-h-[200px]">
        <StartLockedOverviewRow showGoals={showGoals} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StartLockedWhatsNewCard />
          {rightCard === 'hiring' ? (
            <StartLockedHiringStatusCard />
          ) : (
            <StartLockedImplementationCard />
          )}
        </div>
      </div>
    </div>
  );
};

export default StartLockedDepartmentPage;
